import React, { useState, useEffect } from 'react';
import { useClient } from '../../context/ClientContext';

export default function BudgetPacing() {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetcher.fetchBudgetPacing()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!data?.available) return null;

  const primaryColor = brand.colors?.primary || '#2563eb';
  const statusColors = {
    'on-track': { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'On Track' },
    'over': { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Over Pace' },
    'under': { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Under Pace' }
  };
  const status = statusColors[data.status] || statusColors['on-track'];

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide"
              style={{ fontFamily: brand.typography?.headerFont }}>
            Budget Pacing
          </h3>
          <p className="text-xs text-gray-400 mt-1">Day {data.dayOfMonth} of {data.daysInMonth}</p>
        </div>
        <span className={`${status.bg} ${status.text} text-xs font-medium px-2.5 py-1 rounded-full`}>
          {status.label}
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative">
        <div className="w-full bg-gray-100 rounded-full h-4">
          <div
            className="h-4 rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(data.pacePct, 100)}%`,
              backgroundColor: primaryColor
            }}
          />
        </div>
        {/* Expected pace marker */}
        <div
          className="absolute top-0 h-4 border-l-2 border-dashed border-gray-400"
          style={{ left: `${Math.min(data.expectedPct, 100)}%` }}
          title={`Expected: ${data.expectedPct.toFixed(0)}%`}
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mt-4">
        <div>
          <p className="text-lg font-bold text-gray-900">{data.currency}{data.spent.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</p>
          <p className="text-xs text-gray-500">Spent</p>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-900">{data.currency}{data.monthlyBudget.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</p>
          <p className="text-xs text-gray-500">Budget</p>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-900">{data.currency}{data.projectedSpend.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</p>
          <p className="text-xs text-gray-500">Projected</p>
        </div>
      </div>
    </div>
  );
}
